import { useMemo } from "react";
import { QueryParams } from "../types";
import {
  ensureParamIsArray,
  appendQueryParam,
  removeQueryParam,
} from "../utils/urlHelper";

export const PAGE_PARAM_NAME = "page";
export const PAGE_SIZE_PARAM_NAME = "pageSize";
export const ORDER_PARAM_NAME = "order";
export const ORDER_DIRECTION_PARAM_NAME = "orderDirection"

export type SearchBackingState = {
  searchParams: QueryParams;
  navigate: (newParams: QueryParams) => void;
};

export const useSearch = ({ searchParams, navigate }: SearchBackingState) => {
  const searchParamsFiltersOnly = useMemo(() => {
    const {
      [PAGE_PARAM_NAME]: page,
      [PAGE_SIZE_PARAM_NAME]: pageSize,
      [ORDER_PARAM_NAME]: order,
      [ORDER_DIRECTION_PARAM_NAME]: orderDirection,
      ...rest
    } = searchParams;
    return rest;
  }, [searchParams]);

  const navigateToNextSearch = (newParams: QueryParams) => {
    const nextParams: { [key: string]: any } = {
      ...searchParams,
      ...newParams,
      [PAGE_PARAM_NAME]: undefined, // if on a page and search changes, go back to first page
    };

    // remove any undefined params, setting to undefined instructs us to remove the param
    Object.keys(nextParams).forEach(
      (key) => nextParams[key] === undefined && delete nextParams[key]
    );

    navigate(nextParams);
  };

  const navigateToNextSearchArrayParam = (
    searchParamName: string,
    param: { name: string; checked: boolean }
  ) => {
    const nextParams = param.checked
      ? appendQueryParam(searchParams, searchParamName, param.name)
      : removeQueryParam(searchParams, searchParamName, param.name);

    navigateToNextSearch(nextParams);
  };

  const navigateToNextPage = (newParams: QueryParams) => {
    navigate({ ...searchParams, ...newParams });
  };

  const isArrayParamPresent = (param: any, paramValue: string) =>
    ensureParamIsArray(param).includes(paramValue);

  return {
    searchParams,
    searchParamsFiltersOnly,
    navigateToNextSearch,
    navigateToNextSearchArrayParam,
    navigateToNextPage,
    isArrayParamPresent,
  };
};
